import React, { ReactElement, useCallback, useState, useMemo, useEffect } from "react";
import { SearchLoaderProps } from "./SearchLoader.types";

const svgStyle = {
    backgroundColor: "transparent",
};

const divStyle = {
    display: "inherit",
};

const lines = Array.from({ length: 8 }).map((_, index) => index);

/**
 * Renders a spinning loader designed for the search component.
 *
 * @param {SearchLoaderProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function SearchLoader({
    activeColor = "#787CDD",
    color = "#CCC",
    holeDiameterRatio = 0.5,
    size = 20,
    speed = 100,
}: SearchLoaderProps): ReactElement | null {
    const [activeIndex, setActiveIndex] = useState(0);

    const {
        center,
        segmentLength,
        segmentThickness,
        x,
        y,
    } = useMemo(() => {
        const center = size / 2;
        const holeRadius = (size * holeDiameterRatio) / 2;
        const segmentLength = center - holeRadius;
        const segmentThickness = size * 0.1;

        return {
            center,
            segmentLength,
            segmentThickness,
            x: center - segmentThickness / 2,
            y: center - holeRadius - segmentLength,
        };
    }, [size]);

    const renderLine = useCallback((index: number): ReactElement => (
        <rect fill={index === activeIndex ? activeColor : color}
              height={segmentLength}
              key={index}
              rx={segmentThickness / 2}
              transform={`rotate(${index * 45}, ${center}, ${center})`}
              width={segmentThickness}
              x={x}
              y={y} />
    ), [activeColor, activeIndex, color, size]);

    useEffect(function _SearchLoaderMount() {
        const interval = setInterval(function _SearchLoaderInterval() {
            setActiveIndex((prevIndex) => (prevIndex + 1) % lines.length);
        }, speed);

        return function _SearchLoaderUnmount() {
            clearInterval(interval);
        };
    }, [speed]);

    return (
        <div className="SearchLoader" style={divStyle}>
            <svg height={size}
                 style={svgStyle}
                 viewBox={`0 0 ${size} ${size}`}
                 width={size}>
                {lines.map(renderLine)}
            </svg>
        </div>
    );
}

export default SearchLoader;

